import { useLayoutEffect, useRef, useState } from "react";
import type { Intent } from "../types.ts";

interface Props {
  series: number[];
  timestamps?: number[];
  unit?: string;
  intent?: Intent;
  /** Deploy marker timestamps (epoch ms) within the window. */
  deploys?: number[];
  height?: number;
}

const M = { top: 8, right: 6, bottom: 16, left: 34 };

function fmtAge(s: number): string {
  return s >= 86400 ? `${(s / 86400).toFixed(1)}d`
    : s >= 3600 ? `${(s / 3600).toFixed(1)}h`
    : s >= 60 ? `${Math.round(s / 60)}m`
    : `${Math.round(s)}s`;
}
function fmtVal(v: number, unit?: string): string {
  if (unit === "ms") return `${v >= 100 ? Math.round(v) : v.toFixed(1)}ms`;
  if (unit === "%") return `${v.toFixed(1)}%`;
  if (unit === "GB") return `${v.toFixed(1)}GB`;
  if (unit === "age") return fmtAge(v);
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return a < 10 && v % 1 !== 0 ? v.toFixed(1) : `${Math.round(v)}`;
}
function fmtHM(ms: number): string {
  const d = new Date(ms);
  return `${`${d.getHours()}`.padStart(2, "0")}:${`${d.getMinutes()}`.padStart(2, "0")}`;
}

/** Interactive time-series chart: labeled axes (HTML), gridlines, deploy markers, hover. */
export function Chart({ series, timestamps = [], unit, intent, deploys = [], height = 148 }: Props) {
  const [hi, setHi] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(400);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    setW(Math.max(200, Math.floor(el.clientWidth)));
    const ro = new ResizeObserver((e) => { const w = e[0]?.contentRect.width; if (w) setW(Math.max(200, Math.floor(w))); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = height;
  const data = series.length >= 2 ? series : [series[0] ?? 0, series[0] ?? 0];
  const N = data.length;
  const lo = Math.min(...data);
  const hiV = Math.max(...data);
  let yMin = 0, yMax = hiV;
  if (lo === hiV) { yMax = hiV === 0 ? 1 : hiV * 1.15; }
  else { const p = (hiV - lo) * 0.14; yMax = hiV + p; yMin = Math.max(0, lo - p); }

  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const xAt = (i: number) => M.left + (N < 2 ? plotW / 2 : (i / (N - 1)) * plotW);
  const yAt = (v: number) => M.top + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;

  const line = data.map((v, i) => `${i ? "L" : "M"}${xAt(i).toFixed(1)} ${yAt(v).toFixed(1)}`).join(" ");
  const area = `${line} L${xAt(N - 1).toFixed(1)} ${M.top + plotH} L${xAt(0).toFixed(1)} ${M.top + plotH} Z`;

  const yTicks = [0, 0.33, 0.66, 1].map((f) => yMin + f * (yMax - yMin));
  const ylabW = ((M.left - 6) / W) * 100;
  // X labels: always start + end; add the midpoint only when there's a real
  // middle index AND enough width for three ~HH:MM labels not to collide.
  // (For 2-point/flat charts Math.floor((N-1)/2) is 0, which would stack the
  //  middle label on top of the start label — the overlap we're avoiding.)
  const LABELW = 40; // px reserved per HH:MM label
  const midIdx = Math.floor((N - 1) / 2);
  const showMid = midIdx > 0 && midIdx < N - 1 && plotW > LABELW * 3;
  const xLabels: Array<{ i: number; align: "start" | "mid" | "end" }> = showMid
    ? [{ i: 0, align: "start" }, { i: midIdx, align: "mid" }, { i: N - 1, align: "end" }]
    : [{ i: 0, align: "start" }, { i: N - 1, align: "end" }];

  // deploy markers: map timestamp → nearest index x
  const t0 = timestamps[0], tN = timestamps[timestamps.length - 1];
  const markX = (t0 != null && tN != null && tN > t0)
    ? deploys.filter((d) => d >= t0 && d <= tN).map((d) => xAt(Math.round(((d - t0) / (tN - t0)) * (N - 1))))
    : [];

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const frac = Math.min(1, Math.max(0, (px - M.left) / plotW));
    setHi(Math.round(frac * (N - 1)));
  };

  const bad = intent === "bad" || intent === "warn";
  const hoverX = hi != null ? xAt(hi) : 0;
  const hoverV = hi != null ? data[hi] ?? 0 : 0;
  const hoverTs = hi != null ? timestamps[hi] : undefined;
  const frac = hoverX / W;
  const tipShift = frac > 0.62 ? "calc(-100% - 8px)" : frac < 0.08 ? "0" : "-50%";

  return (
    <div className={`chartc${bad ? " bad" : ""}`} ref={wrapRef}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img"
        onMouseMove={onMove} onMouseLeave={() => setHi(null)}>
        {yTicks.map((v, i) => (
          <line key={i} className="grid" x1={M.left} x2={W - M.right} y1={yAt(v)} y2={yAt(v)} />
        ))}
        <path className="area" d={area} />
        <path className="cline" d={line} />
        {markX.map((x, i) => <line key={i} className="mark" x1={x} x2={x} y1={M.top} y2={M.top + plotH} />)}
        {hi != null && (
          <>
            <line className="crosshair" x1={hoverX} x2={hoverX} y1={M.top} y2={M.top + plotH} />
            <circle className="hdot" cx={hoverX} cy={yAt(hoverV)} r="3.5" fill={bad ? "var(--red)" : "var(--accent)"} />
          </>
        )}
      </svg>
      {yTicks.map((v, i) => (
        <span key={i} className="ylab" style={{ width: `${ylabW}%`, top: `${yAt(v)}px` }}>{fmtVal(v, unit)}</span>
      ))}
      {xLabels.map(({ i: idx, align }, k) => (
        <span key={k} className="xlab" style={{
          left: `${(xAt(idx) / W) * 100}%`,
          transform: `translateX(${align === "start" ? "0" : align === "end" ? "-100%" : "-50%"})`,
        }}>{timestamps[idx] != null ? fmtHM(timestamps[idx]!) : ""}</span>
      ))}
      {hi != null && (
        <div className="ctip" style={{ left: `${hoverX}px`, transform: `translateX(${tipShift})` }}>
          <b>{fmtVal(hoverV, unit)}</b>
          {hoverTs != null && <span>{fmtHM(hoverTs)}</span>}
        </div>
      )}
    </div>
  );
}
